import { Box, Stack, Table, Text } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type SortingState,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { type SchemaListItem } from '@morph-mapper/types';
import { PagePanel } from '../../common';
import {
  StyledBodyRow,
  StyledEmptyStateCell,
  StyledHeaderCell,
  StyledTableShell,
} from './styles';

type SchemaListTableProps = {
  columns: Array<ColumnDef<SchemaListItem>>;
  data: SchemaListItem[];
  onSortingChange: OnChangeFn<SortingState>;
  sorting: SortingState;
};

export const SchemaListTable = ({
  columns,
  data,
  onSortingChange,
  sorting,
}: SchemaListTableProps) => {
  const navigate = useNavigate();

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange,
    state: {
      sorting,
    },
  });

  return (
    <PagePanel padding={0} overflow="hidden">
      <StyledTableShell>
        <Table highlightOnHover className="schema-list-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <StyledHeaderCell
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    $canSort={header.column.getCanSort()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </StyledHeaderCell>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <StyledEmptyStateCell colSpan={columns.length}>
                  <Box py={60}>
                    <Stack align="center" spacing="xs">
                      <Text size="sm" c="dimmed">
                        No schemas found
                      </Text>
                      <Text size="xs" c="dimmed">
                        Try adjusting your filters or create a new schema
                      </Text>
                    </Stack>
                  </Box>
                </StyledEmptyStateCell>
              </tr>
            ) : null}

            {table.getRowModel().rows.map((row) => (
              <StyledBodyRow
                key={row.id}
                onClick={() =>
                  void navigate({
                    params: { schemaId: row.original.id },
                    to: '/schemas/$schemaId',
                  })
                }
                $isDraft
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    onClick={(event) => {
                      // Do not redirect to schema definition when interacting with a row.
                      if (
                        cell.column.id === 'actions' ||
                        cell.column.id === 'status'
                      ) {
                        event.stopPropagation();
                      }
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </StyledBodyRow>
            ))}
          </tbody>
        </Table>
      </StyledTableShell>
    </PagePanel>
  );
};
